import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { Toast } from "@egreact/shared";
import Popup from "../Popup/Popup";
import {
  Platform,
  isAdmin,
  hasCustomerLogin,
  getLoginPage,
  fetchInstallmentCoinsApi,
  applyInstallmentCoinsApi,
} from "../../script/util";
import "./InstallmentCoins.css";

const COINS = Platform.isWG ? "WG Coins" : "EG Coins";

// On a root of its own rather than inside the box: coins that cover the
// installment mark it paid, the quotation reloads, and this row's box is
// unmounted, which would take a toast rendered inside it along too.
function showToast(message) {
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const close = () => {
    root.unmount();
    host.remove();
  };
  root.render(<Toast message={message} onClose={close} />);
}

/**
 * EG Coins on the next installment due, under that row in the payment
 * policy. The row itself prints the coins already on it under its name,
 * paid or not; this is only the apply box.
 *
 * There is no amount to enter: one tap uses the whole balance, or just
 * enough to cover what is left of the installment, which the server works
 * out. Applying is final, so it asks for confirmation first and there is
 * no remove, but more coins can go on while the installment has an amount
 * left. The server decides who sees the box (owner, wallet, next
 * installment, nothing pending verification), so a co-admin or an empty
 * wallet never does. A proposal opened without a login (a magic link) is
 * shown a prompt to log in instead, when the customer has a coin to spend. `onChange` reloads the quotation so
 * every figure on the page moves together.
 *
 * The popup is portalled to the body and the toast has its own root: the
 * payment policy rows force a large font size, and inside them both
 * rendered oversized next to the same components on other pages.
 */
const InstallmentCoins = ({ installmentId, onChange, canUseAfterLogin }) => {
  const [info, setInfo] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAdmin() || !hasCustomerLogin() || !installmentId) return;
    fetchInstallmentCoinsApi(installmentId)
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [installmentId]);

  // A proposal opened from a magic link carries no login, and coins come out
  // of a wallet, so there is nobody to take them from yet. Prompted only when
  // the quotation says the customer has a valid coin to spend here
  // (`canUseAfterLogin`), since a login that leads to nothing is noise.
  // Login brings them back to this proposal (getLoginPage keeps it as the
  // next url), where the server then decides whether the box is theirs.
  if (!isAdmin() && !hasCustomerLogin()) {
    if (!canUseAfterLogin) return null;
    return (
      <div className="inst-coins inst-coins-login">
        <span>Log in to use your {COINS} on this installment.</span>
        <button className="inst-coins-open" onClick={() => getLoginPage()}>
          Log in
        </button>
      </div>
    );
  }

  // The coins already on the row are printed under its name by the row
  // itself, for paid rows too, so the box does not repeat them.
  if (!info || !info.can_apply) return null;

  const apply = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const data = await applyInstallmentCoinsApi(installmentId);
      setConfirming(false);
      setInfo(data);
      showToast({ title: data.message || `${COINS} applied.` });
      if (onChange) await onChange();
    } catch (err) {
      setConfirming(false);
      setError(err?.response?.data?.message ||
        `${COINS} could not be applied. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="inst-coins">
      <button
        className="inst-coins-open"
        onClick={() => { setError(""); setConfirming(true); }}
        disabled={loading}
      >
        {/* Ticked while the confirm is open; cancelling clears it again. */}
        <span className={`inst-coins-tick${confirming ? " checked" : ""}`} aria-hidden="true">
          {confirming && "✓"}
        </span>
        Use {info.max_coins} {COINS} (₹{info.max_coins_inr} off)
      </button>
      {error && (
        <div className="coupon-message coupon-message--error">{error}</div>
      )}
      {createPortal(<Popup
        isOpen={confirming}
        onClose={loading ? null : () => setConfirming(false)}
      >
        <div className="inst-coins-confirm">
          <h3>Use {info.max_coins} {COINS}?</h3>
          <p>
            ₹{info.max_coins_inr} will come off this installment. Once
            applied, you cannot remove {COINS} from it.
          </p>
          <div className="inst-coins-confirm-actions">
            <button
              className="inst-coins-cancel"
              onClick={() => setConfirming(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="coupon-apply-btn"
              onClick={apply}
              disabled={loading}
            >
              {loading ? "Applying..." : "Yes, apply"}
            </button>
          </div>
        </div>
      </Popup>, document.body)}
    </div>
  );
};

export default InstallmentCoins;
